import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { XMarkIcon, PlusIcon, ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import Modal from '../common/Modal';
import { Input } from '../common/Input';
import { Textarea } from '../common/Textarea';
import { Button } from '../common/Button';
import { Select } from '../common/Select';
import { Checkbox } from '../common/Checkbox';
import { workflowService, type Workflow, type WorkflowStep } from '../../services/workflowService';
import api from '../../services/api';

interface WorkflowFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (workflow: Workflow) => void;
  isLoading: boolean;
  title: string;
  workflow?: Workflow;
}

const approverTypes = [
  { value: 'manager', label: 'Manager' },
  { value: 'role', label: 'Role' },
  { value: 'user', label: 'Specific User' }
];

const WorkflowForm: React.FC<WorkflowFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  title,
  workflow
}) => {
  const [roles, setRoles] = useState<Array<{ id: number; name: string }>>([]);
  const [users, setUsers] = useState<Array<{ id: number; first_name: string; last_name: string; email: string }>>([]);
  
  const {
    register,
    handleSubmit,
    control,
    formState: { errors }
  } = useForm<Workflow>({
    defaultValues: workflow || {
      name: '',
      description: '',
      is_active: true,
      steps: []
    }
  });

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'steps'
  });

  // Fetch roles
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await api.get('/roles');
        setRoles(response.data);
      } catch (error) {
        console.error('Error fetching roles:', error);
      }
    };
    
    fetchRoles();
  }, []);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get('/users');
        setUsers(response.data.users);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    
    fetchUsers();
  }, []);

  // Fetch leave types
  const { data: leaveTypes = [] } = useQuery({
    queryKey: ['leaveTypes'],
    queryFn: async () => {
      const response = await api.get('/leave-types');
      return response.data;
    }
  });

  // Add a new step
  const addStep = () => {
    append({
      name: `Step ${fields.length + 1}`,
      description: '',
      order: fields.length,
      approver_type: 'manager'
    });
  };

  // Move step up or down
  const moveStep = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index > 0) {
      move(index, index - 1);
    } else if (direction === 'down' && index < fields.length - 1) {
      move(index, index + 1);
    }
  };

  // Handle form submission
  const handleFormSubmit = (data: Workflow) => {
    // Update step orders based on their position in the array
    const updatedData = {
      ...data,
      steps: data.steps?.map((step, index) => ({
        ...step,
        order: index
      }))
    };
    
    onSubmit(updatedData);
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={title}
      size="lg"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="space-y-6">
          <Input
            label="Workflow Name"
            {...register('name', { required: 'Workflow name is required' })}
            error={errors.name?.message}
          />
          
          <Textarea
            label="Description"
            {...register('description')}
            error={errors.description?.message}
          />

          <div className="flex items-center">
            <Checkbox
              label="Active"
              {...register('is_active')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Leave Type (Optional)
            </label>
            <Controller
              name="leave_type_id"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value?.toString()}
                  onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                >
                  <option value="">Select a leave type</option>
                  {leaveTypes.map((type: any) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </Select>
              )}
            />
            <p className="mt-1 text-xs text-gray-500">
              Assign this workflow to a specific leave type
            </p>
          </div>

          <div className="border-t pt-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Approval Steps</h3>
              <Button 
                type="button" 
                onClick={addStep} 
                variant="secondary"
                className="flex items-center"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Step
              </Button>
            </div>

            {fields.length === 0 ? (
              <div className="text-center py-6 bg-gray-50 rounded-md border border-dashed">
                <p className="text-gray-500 mb-2">No approval steps defined</p>
                <Button 
                  type="button" 
                  onClick={addStep} 
                  variant="secondary"
                  className="inline-flex items-center"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Add First Step
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {fields.map((field, index) => (
                  <div 
                    key={field.id} 
                    className="border rounded-md p-4 bg-gray-50"
                  >
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-medium">Step {index + 1}</h4>
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={() => moveStep(index, 'up')}
                          className="text-gray-400 hover:text-gray-600"
                          disabled={index === 0}
                        >
                          <ArrowUpIcon className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveStep(index, 'down')}
                          className="text-gray-400 hover:text-gray-600"
                          disabled={index === fields.length - 1}
                        >
                          <ArrowDownIcon className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          className="text-red-400 hover:text-red-600"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label="Step Name"
                        {...register(`steps.${index}.name`, { required: 'Step name is required' })}
                        error={errors.steps?.[index]?.name?.message}
                      />
                      
                      <Controller
                        name={`steps.${index}.approver_type`}
                        control={control}
                        rules={{ required: 'Approver type is required' }}
                        render={({ field }) => (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Approver Type
                            </label>
                            <Select
                              value={field.value}
                              onChange={(e) => field.onChange(e.target.value)}
                              error={errors.steps?.[index]?.approver_type?.message}
                            >
                              {approverTypes.map((type) => (
                                <option key={type.value} value={type.value}>
                                  {type.label}
                                </option>
                              ))}
                            </Select>
                          </div>
                        )}
                      />
                      
                      {/* Conditional approver selection based on type */}
                      {fields[index].approver_type === 'role' && (
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Select Role
                          </label>
                          <Controller
                            name={`steps.${index}.approver_id`}
                            control={control}
                            rules={{ required: 'Role is required' }}
                            render={({ field }) => (
                              <Select
                                value={field.value?.toString()}
                                onChange={(e) => field.onChange(Number(e.target.value))}
                                error={errors.steps?.[index]?.approver_id?.message}
                              >
                                <option value="">Select a role</option>
                                {roles.map((role) => (
                                  <option key={role.id} value={role.id}>
                                    {role.name}
                                  </option>
                                ))}
                              </Select>
                            )}
                          />
                        </div>
                      )}
                      
                      {fields[index].approver_type === 'user' && (
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Select User
                          </label>
                          <Controller
                            name={`steps.${index}.approver_id`}
                            control={control}
                            rules={{ required: 'User is required' }}
                            render={({ field }) => (
                              <Select
                                value={field.value?.toString()}
                                onChange={(e) => field.onChange(Number(e.target.value))}
                                error={errors.steps?.[index]?.approver_id?.message}
                              >
                                <option value="">Select a user</option>
                                {users.map((user) => (
                                  <option key={user.id} value={user.id}>
                                    {user.first_name} {user.last_name} ({user.email})
                                  </option>
                                ))}
                              </Select>
                            )}
                          />
                        </div>
                      )}
                      
                      <Textarea
                        label="Description (Optional)"
                        className="md:col-span-2"
                        {...register(`steps.${index}.description`)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="mt-8 flex justify-end space-x-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isLoading}
          >
            {workflow ? 'Update Workflow' : 'Create Workflow'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default WorkflowForm;